import React, { useState, useRef, useEffect } from "react";

import {
    Box,
    Divider,
    TextField,
    Grid,
    Typography,
    Button,
    IconButton,
    InputAdornment,
    Container,
    Card,
    styled,
    FormHelperText,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Chip
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import dayjs from "dayjs";

import { useNavigate, useSearchParams, useParams } from 'react-router-dom';

import { useFormik } from 'formik';

import { useAppContextFeatures } from "../../../context/AppContext";
import { useAuthAccess } from "../../../hooks/useAuthAccess";

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from "../../common/CommonRestricted";
import DatePickerComponent from "../../common/DatePickerComponent";

import SearchCustomers from "../../dialog/SearchCustomers";
import SearchItems from "../../dialog/SearchItems";
import SearchRetailers from "../../dialog/SearchRetailers";

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";
import { useTranslations } from '../../../hooks/useTranslations';

import { CommonIcons, icons } from "../../common/CommonIcons";
import { useDocuments, documentTypes, targetTypes } from "../../../hooks/api/useDocuments";
import { useUsers, userTypes } from "../../../hooks/api/useUsers";
import { checkValueExists } from "../../../utils/dataSchema";
import { useDocumentAdjustments } from "../../../hooks/state/useDocumentAdjustments";

import {
    validationSchema,
    initialValues,
    initialRowValues,
} from "../../../schema/documents/replacementDeliverySchema";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export const StyledIcon = styled(Box)({
    width: 20,
    height: 20,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
});

export default function ReplacementDeliveryUpdatePage({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const { id } = useParams();

    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const { getAvailableLocations } = useAuthAccess();
    const { getLocationIds, setLocationIds } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.replacementDelivery.id)

    const { setSystemLoading } = useAppContextFeatures();
    const { isLoading, get, update, filterOperatorTypes } = useDocuments({ 
        documentType: documentTypes.replacementDelivery, 
        targetType: targetTypes.replacementDelivery
    })
    const { get: getUser } = useUsers({ userType: userTypes.customer });
    const [savedValues, setSavedValues] = useState()

    const availableLocations = getAvailableLocations();

    const [documentUserType, setDocumentUserType] = useState()

    const [dataBranches, setDataBranches] = useState([])
    const [dataShipping, setDataShipping] = useState([])

    const [preview, setPreview] = useState(false)
    const [selectedAdjustments, setSelectedAdjustments] = useState([])

    const loadData = async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id, dataTypesArray: ["adjustment"] })
            const { document, items } = data;

            // create rows
            const rows = items.map((i) => {
                return {
                    docItemId: i?.id,
                    itemId: i?.item_id,
                    name: i?.item_name,
                    code: i?.code,
                    description: i?.description,
                    qty: i?.qty,
                    uomName: i?.uom_name,
                    uomId: i?.uom_id,
                    price: i?.uom_price,
                    subtotal: i?.sub_price
                }
            })

            let _selectedAdjustments = document?.adjustments
            setSelectedAdjustments(_selectedAdjustments)

            setSavedValues({
                ...initialValues,

                locationId: document?.location_id,
                locationName: document?.location_name,
                documentNumber: document?.doc_number,
                documentDate: dayjs(document?.doc_date),
                remark: document?.doc_remark ? document.doc_remark : "",
                userId: document?.user_id,
                username: document?.profile_name,
                shippingBranchId: document?.replacement_delivery_branch_id,
                shippingBranchName: document?.replacement_delivery_profile_name,
                billingBranchId: document?.billing_branch_id,
                billingBranchName: document?.billing_profile_name,

                codEnabled: document?.is_payment_collect === "1",
                codRequiredAmount: document?.require_collect_amount,
                codCollectedAmount: document?.collection_amount,
                totalamount: document?.total_amount,

                rows,

            })

            setDocumentUserType(document?.user_type)

            // get the branches
            if (document?.user_id) {
                fetchBranches({ userType: document?.user_type, userId: document?.user_id })
            }

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    const onSubmit = async (currentValues) => {

        const document = {
            doc_date: currentValues.documentDate.format("YYYY-MM-DD"),
            doc_remark: currentValues.remark,
        }

        const profiles = {
            customer: {
                user_id: currentValues.userId
            },
            shipping_address: {
                branch_id: currentValues.shippingBranchId
            },
            billing_branch: {
                branch_id: currentValues.billingBranchId
            }
        }

        // if(documentUserType === userTypes.customer){
        //     profiles.customer = {
        //         user_id: currentValues.userId
        //     }
        // }

        const props = {
            location_id: currentValues.locationId,
            collection_remark: ""
        }

        const amounts = {
            require_collect_amount: currentValues.codRequiredAmount,
            collection_amount: currentValues.codCollectedAmount
        }

        const status = {
            is_payment_collect: currentValues.codEnabled ? 1 : 0,
        }

        let items = [];

        let adjustments = getPayloadSubmitCreate()

        currentValues.rows.map(item => {
            items.push({
                id: item?.itemId,
                doc_item_id: item?.docItemId,
                code: item?.code,
                name: item?.name,
                description: item?.description,
                qty: item?.qty,
                uom_lbl: item?.uomName,
                uom: item?.uomId,
                price: item?.price,
                props: {
                    scan_type: 0,
                }
            })
            return false;
        })

        try {

            let data = {
                document,
                profiles,
                amounts,
                status,
                props,
                items,
                adjustments,
            }

            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)

            navigate(getRoute(`documents/replacementDelivery/view/${id}`))

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

    }

    const fetchBranches = async ({ userType, userId }) => {

        try {

            if (userType === userTypes.customer) {
                const { data } = await getUser({ id: userId, userType: userTypes.customer, dataTypesArray: ["branch", "shipping_address"] })

                setDataBranches(data?.customer?.branches ? data.customer.branches : [])
                setDataShipping(data?.customer?.shipping_addresses ? data.customer.shipping_addresses : [])
            }

            if (userType === userTypes.retail) {
                const { data } = await getUser({ id: userId, userType: userTypes.retail, dataTypesArray: ["branch", "shipping_address"] })

                setDataBranches(data?.retailer?.branches ? data.retailer.branches : [])
                setDataShipping(data?.retailer?.shipping_addresses ? data.retailer.shipping_addresses : [])
            }


        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { handleSubmit, handleChange, values, errors, touched, setFieldValue, } = formik;

    const {
        // getAmount,
        calculateSummaryRows,
        updateTaxPreviewOpen,
        RenderDocumentSummary,
        RenderDocumentTaxPreview,
        getPayloadSubmitCreate,
        updateAdjustments
    } = useDocumentAdjustments({
        formik,
        documentType: documentTypes.replacementDelivery,
        handlePreviewChange: (val) => {
            // console.log('current open', val)
            setPreview(val)
        }
    })

    useEffect(() => {

        updateTaxPreviewOpen(preview)

    }, [preview])

    useEffect(() => {

        if (selectedAdjustments.length > 0)
            updateAdjustments(selectedAdjustments)

    }, [selectedAdjustments])

    useEffect(() => {

        calculateSummaryValues();

    }, [values])

    useEffect(() => {

        loadData(id);

    }, [])

    // useEffect(() => {

    //     const searchParamsAction = searchParams.get('action');
    //     const searchParamsPayload = searchParams.get('payload');

    //     // console.log('searchParams', searchParamsAction, searchParamsPayload)

    //     if(searchParamsAction && searchParamsAction.toLowerCase() === 'duplicate'){
    //         setSearchDocumentConfig(prev => ({ ...prev, show: true, documentType: documentTypes.replacementDelivery, keyword: searchParamsPayload}))
    //     }

    // }, [])

    // const updateDefaultFields = () => {

    //     // update the auth data
    //     // setFieldValue("retailOutletUserId", authData?.profile?.user?.id)

    //     // get use the parent user id
    //     setFieldValue('userId', authData?.profile?.parent_user_id)

    //     if(selectLocationIds && selectLocationIds.length){
    //         const check = availableLocations.filter(i => i.id === selectLocationIds[0])
    //         // console.log('check', check)
    //         if(check.length){

    //             // use the selected one branch location id
    //             setFieldValue('shippingBranchId', check[0].branch_id)

    //             // use the selected one brandh billing branch id
    //             setFieldValue('billingBranchId', check[0].billing_branch_id)

    //         }
    //     }

    // }

    // useEffect(() => {

    //     updateDefaultFields()

    // }, [selectLocationIds])

    // useEffect(() => {

    //     const currentLocationIds = getLocationIds();

    //     // setSelectLocationIds(currentLocationIds)

    //     if(currentLocationIds.length > 0){
    //         // console.log('currentLocationIds', currentLocationIds, availableLocations)

    //         // get the location information
    //         const loc = availableLocations.filter(item => item.id === currentLocationIds[0])

    //         if(loc.length){
    //             setFieldValue("locationId", loc[0].id)
    //             setFieldValue("locationName", loc[0].location_name)
    //         }
    //     }

    // }, [getLocationIds])

    // const validateLocationSelection = () => {
    //     // return (selectLocationIds && selectLocationIds.length === 1)
    //     const selected = getLocationIds()
    //     return selected && selected.length === 1
    // }

    // const updateStateFromSearchDocument = ({ document }) => {

    //     // check the location is selected - valid
    //     if(validateLocationSelection() === false){
    //         promptMessage(false, `Please select any location`)
    //         return false;
    //     }

    //     console.log('document', document)

    //     const {
    //         document: loadDocument,
    //         items: loadItems
    //     } = document

    //     // only load these fields
    //     setFieldValue(`remark`, loadDocument?.doc_remark? loadDocument.doc_remark : "")
    //     // setFieldValue(`discount`, loadDocument?.discount_amount)

    //     loadItems.forEach((e, index) => {
    //         setFieldValue(`rows.${index}.itemId`, e?.item_id)
    //         setFieldValue(`rows.${index}.name`, e?.item_name)
    //         setFieldValue(`rows.${index}.code`, e?.code)
    //         setFieldValue(`rows.${index}.description`, e?.description)
    //         setFieldValue(`rows.${index}.qty`, e?.qty)
    //         setFieldValue(`rows.${index}.uomId`, e?.uom_id)
    //         setFieldValue(`rows.${index}.uomName`, e?.uom_name)
    //         setFieldValue(`rows.${index}.price`, e?.uom_price)
    //     })

    //     promptMessage(true, `Data loaded from ${loadDocument?.doc_number}`)

    //     return true;
    // }

    const calculateSummaryValues = () => {

        if (!values) return false;

        try {
            calculateSummaryRows({
                handleValueRow: ({ subTotal, index }) => {
                    setFieldValue(`rows.${index}.subtotal`, subTotal.toFixed(2))
                }
            })
        } catch (error) {
            promptMessage(false, `Error in summary calculation : ${error}`)
        }

        return true;

    }

    const handleAddItem = ({ rows, setFieldValue }) => {

        rows.push(initialRowValues);
        setFieldValue('rows', rows)

    }

    const handleRemoveItem = ({ rows, index, setFieldValue }) => {

        const currentLine = rows ? rows.length : 0;

        if (currentLine <= 1) {
            promptMessage(false, t(`itemsAtLeastHave1Row`))
            return false;
        }

        const newRows = rows.filter((i, ind) => ind !== index)

        // rows.remove(index);
        setFieldValue('rows', newRows)

        return true;

    }

    const RenderRows = () => {

        const rows = values?.rows;

        return (
            <Box
            >
                <Box
                >
                    {
                        rows && rows.length > 0
                        && rows.map((item, index) => {

                            const itemValues = item;
                            let itemTouched = touched?.rows
                            let itemErrors = errors?.rows
                            // const itemTouched = touched?.rows

                            if (itemTouched) {
                                itemTouched = (itemTouched[index] !== undefined) ? itemTouched[index] : {};
                            }

                            if (itemErrors) {
                                itemErrors = (itemErrors[index] !== undefined) ? itemErrors[index] : {};
                            }

                            return (
                                <Grid key={index} item xs={12} sm={12} md={12}>

                                    <Grid container spacing={0.5} >

                                        <Grid item xs={12} sm={12} md={0.5} alignSelf={"center"}>
                                            <IconButton
                                                aria-label="delete"
                                                onClick={() => handleRemoveItem({ rows: values.rows, index, setFieldValue })}
                                            >
                                                <CommonIcons name={icons.remove} />
                                            </IconButton>
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={3.5}>

                                            <SearchItems
                                                id={`rows.${index}.name`}
                                                handleOnCancel={() => { }}
                                                handleOnSearchChange={(event) => {
                                                    setFieldValue(`rows.${index}.name`, `${event.target.value}`)
                                                }}
                                                handleOnSelect={({ id, item }) => {
                                                    // console.log('id', id, item)
                                                    setFieldValue(`rows.${index}.itemId`, item?.id)
                                                    setFieldValue(`rows.${index}.name`, item?.item_name)
                                                    setFieldValue(`rows.${index}.description`, item?.description)
                                                    setFieldValue(`rows.${index}.code`, item?.code)
                                                    setFieldValue(`rows.${index}.uomName`, item?.uom_name)
                                                    setFieldValue(`rows.${index}.uomId`, item?.uom_id)
                                                    setFieldValue(`rows.${index}.qty`, 1)
                                                    setFieldValue(`rows.${index}.price`, item?.uom_price)
                                                }}
                                                textProps={{
                                                    disabled: true,
                                                    value: itemValues?.name ? itemValues.name : "",
                                                    error: Boolean(itemErrors?.name),
                                                    helperText: itemErrors?.name
                                                }}
                                            />

                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1}>
                                            <TextField
                                                disabled
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.uomName`}
                                                label={t("uom")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.uomName ? itemValues.uomName : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.uomName)}
                                                helperText={itemErrors?.uomName}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={3.5}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.description`}
                                                label={t("description")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.description ? itemValues.description : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.description)}
                                                helperText={itemErrors?.description}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1}>
                                            <TextField
                                                inputProps={{
                                                    style: {
                                                        textAlign: "center"
                                                    }
                                                }}
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.qty`}
                                                label={t("qty")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.qty ? itemValues.qty : ""}
                                                onChange={(event) => {
                                                    setFieldValue(`rows.${index}.qty`, event.target.value);
                                                    // handleDisplayRowChange({ index, qty: event.target.value});
                                                }}
                                                error={Boolean(itemErrors?.qty)}
                                                helperText={itemErrors?.qty}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1}>
                                            <TextField
                                                inputProps={{
                                                    style: {
                                                        textAlign: "right"
                                                    }
                                                }}
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.price`}
                                                label={t("price")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.price ? itemValues.price : ""}
                                                onChange={(event) => {
                                                    setFieldValue(`rows.${index}.price`, event.target.value);
                                                    // handleDisplayRowChange({ index, price: event.target.value});
                                                }}
                                                error={Boolean(itemErrors?.price)}
                                                helperText={itemErrors?.price}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1.5}>
                                            <TextField
                                                inputProps={{
                                                    style: {
                                                        textAlign: "right"
                                                    }
                                                }}
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                disabled
                                                name={`rows.${index}.subtotal`}
                                                label={t("subTotal")}
                                                value={itemValues?.subtotal ? itemValues.subtotal : "0.00"}
                                                type="text"
                                                variant="outlined"
                                            />
                                        </Grid>

                                    </Grid>
                                </Grid>
                            )
                        })
                    }
                </Box>

                <Grid item xs={12}>
                    <Divider sx={{ mt: 1, mb: 0 }} />
                </Grid>

                {
                    RenderSummaryRow({ values, errors, touched, handleChange, setFieldValue })
                }

            </Box>
        )
    }

    // Tax & discount
    const RenderSummaryRow = ({ values, errors, touched, handleChange, setFieldValue }) => {
        return (
            <Grid item xs={12} sm={12} md={12}>

                <Grid container spacing={0.5}>

                    <Grid item xs={12} sm={12} md={4}>

                        <Button
                            variant="outlined"
                            sx={{ mt: 2 }}
                            fullWidth
                            startIcon={<CommonIcons name={icons.add} />}
                            onClick={() => handleAddItem({ rows: values.rows, setFieldValue })}
                        >
                            {t('addItem')}
                        </Button>

                    </Grid>

                    <Grid item xs={12} sm={12} md={3}>
                        <></>
                    </Grid>

                    <Grid item xs={12} sm={12} md={5}>

                        {
                            RenderDocumentSummary()
                        }

                    </Grid>

                </Grid>

            </Grid>
        )
    }

    const RenderFormLocation = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`location`)}
                            subTitle={t(`relatedWarehouseLocationToProcess`)}
                        />

                    </Grid>

                    <Grid item xs={12} sm={12} md={10.5}>
                        <TextField
                            disabled
                            margin="dense"
                            size="small"
                            fullWidth
                            name={"locationId"}
                            label={t("location")}
                            type="text"
                            variant="outlined"
                            value={values.locationName}
                            error={touched.locationId && Boolean(errors.locationId)}
                            helperText={touched.locationId && errors.locationId}
                        />
                    </Grid>
                    <Grid item md={1.5}>
                        <IconButton
                            color="primary"
                            sx={{
                                // pt: 0
                            }}
                            onClick={() => {
                                setLocationIds([])
                            }}
                        >
                            <StyledIcon
                                sx={{
                                    width: 25,
                                    height: 25,
                                    m: 0,
                                    p: 0
                                }}
                            >
                                <CommonIcons name={icons.edit} />
                            </StyledIcon>
                        </IconButton>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormProfiles = () => {
        return (
            <StyledCard sx={{ mt: 1 }}>

                <Grid container spacing={0.5}>
                    <Grid item xs={6} sm={6} md={6} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`profile`)}
                            subTitle={t(`relatedProfilesToProcess`)}
                        />
                    </Grid>

                    <Grid item xs={6} sm={6} md={6} textAlign={"right"}>

                        <Chip label={documentUserType} sx={{ textTransform: 'capitalize' }} size="small" variant="outlined" color={'primary'} />

                    </Grid>

                    <Grid item xs={12} sm={12} md={12}>

                        {
                            documentUserType === 'customer' &&

                            <SearchCustomers
                                handleOnCancel={() => {

                                }}
                                handleOnSelect={(props) => {
                                    console.log('props', props)

                                    // reset state
                                    setDataBranches([])
                                    setDataShipping([])

                                    const { id, item } = props
                                    setFieldValue('username', item?.company_name)
                                    setFieldValue('userId', item?.user_id)

                                    // update state
                                    setDataBranches(item?.branches)
                                    setDataShipping(item?.shipping_addresses)

                                }}
                                textProps={{
                                    disabled: true,
                                    value: values.username,
                                    error: Boolean(errors?.userId),
                                    helperText: errors?.userId
                                }}
                            />

                        }

                        {
                            documentUserType === 'retailer' &&

                            <SearchRetailers
                                handleOnCancel={() => {

                                }}
                                handleOnSelect={(props) => {

                                    // reset state
                                    setDataBranches([])
                                    setDataShipping([])
                                    setFieldValue('billingBranchId', "")
                                    setFieldValue('shippingBranchId', "")

                                    const { id, item } = props
                                    setFieldValue('username', item?.company_name)
                                    setFieldValue('userId', item?.user_id)

                                    // update state
                                    // setDataBranches(item?.branches)
                                    // setDataShipping(item?.shipping_addresses)
                                    fetchBranches({ userType: userTypes.retail, userId: item?.user_id })

                                }}
                                textProps={{
                                    disabled: true,
                                    value: values.username,
                                    error: Boolean(errors?.userId),
                                    helperText: errors?.userId
                                }}
                            />
                        }

                    </Grid>

                    <Grid item xs={12} sm={12} md={12}>
                        <FormControl
                            margin="dense"
                            fullWidth
                            size="small"
                            error={touched.billingBranchId && Boolean(errors.billingBranchId)}
                        >
                            <InputLabel id="rack-label">{t('billTo')}</InputLabel>
                            <Select
                                // labelId="rack-label"
                                // id="rack_id"
                                // value={values?.billingBranchId}
                                name={`billingBranchId`}
                                label={t('billTo')}
                                margin="dense"
                                size="small"
                                onChange={(event) => {
                                    setFieldValue(`billingBranchId`, event.target.value)
                                }}
                                value={
                                    checkValueExists({
                                        searchFieldId: "id",
                                        dataSearch: dataBranches,
                                        valueToCompare: values?.billingBranchId
                                    })
                                }
                            >
                                {
                                    dataBranches && dataBranches.map((item, index) =>
                                        <MenuItem key={index} value={item.id}>{item.branch_name}</MenuItem>
                                    )
                                }
                            </Select>
                            <FormHelperText>
                                {touched.billingBranchId && errors.billingBranchId}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={12} md={12}>
                        <FormControl
                            margin="dense"
                            fullWidth
                            size="small"
                            error={touched.shippingBranchId && Boolean(errors.shippingBranchId)}
                        >
                            <InputLabel id="rack-label">{t('shipTo')}</InputLabel>
                            <Select
                                // labelId="rack-label"
                                // id="rack_id"
                                // value={values?.shippingBranchId}
                                name={`shippingBranchId`}
                                label={t('shipTo')}
                                margin="dense"
                                size="small"
                                onChange={(event) => {
                                    setFieldValue(`shippingBranchId`, event.target.value)
                                }}
                                value={
                                    checkValueExists({
                                        searchFieldId: "id",
                                        dataSearch: dataShipping,
                                        valueToCompare: values?.shippingBranchId
                                    })
                                }
                            >
                                {
                                    dataShipping && dataShipping.map((item, index) =>
                                        <MenuItem key={index} value={item.id}>{item.branch_name}</MenuItem>
                                    )
                                }
                            </Select>
                            <FormHelperText>
                                {touched.shippingBranchId && errors.shippingBranchId}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormCollection = () => {
        return (
            <StyledCard sx={{ mt: 1 }}>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`cashCollection`)}
                            subTitle={t(`optionalForCollectionUponDelivery`)}
                        />

                    </Grid>
                    <Grid item xs={12} sm={12} md={6} textAlign={"right"}>

                        <FormControlLabel
                            control={
                                <Switch
                                    name="codEnabled"
                                    checked={Boolean(values?.codEnabled)}
                                    onChange={(event, checked) => {

                                        if (checked === false) {
                                            setFieldValue(`codRequiredAmount`, "0.00")
                                        }

                                        setFieldValue("codEnabled", checked);

                                    }}
                                />
                            }
                            label={
                                Boolean(values?.codEnabled) ? t("enabled") : t("disabled")
                            }
                        />

                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={1}>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    disabled={values?.codEnabled === false}
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"codRequiredAmount"}
                                    label={t("requiredAmount")}
                                    type="text"
                                    variant="outlined"
                                    value={values.codRequiredAmount}
                                    onChange={handleChange}
                                    error={touched.codRequiredAmount && Boolean(errors.codRequiredAmount)}
                                    helperText={touched.codRequiredAmount && errors.codRequiredAmount}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    disabled={values?.codEnabled === false}
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"codCollectedAmount"}
                                    label={t("collectedAmount")}
                                    type="text"
                                    variant="outlined"
                                    value={values.codCollectedAmount}
                                    onChange={handleChange}
                                    error={touched.codCollectedAmount && Boolean(errors.codCollectedAmount)}
                                    helperText={touched.codCollectedAmount && errors.codCollectedAmount}
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDocument = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`document`)}
                            subTitle={t(`basicInformationAboutTheDocument`)}
                        />

                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={1}>

                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "documentDate",
                                        label: t("documentDate")
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"remark"}
                                    label={t("remark")}
                                    type="text"
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    value={values.remark}
                                    onChange={handleChange}
                                    error={touched.remark && Boolean(errors.remark)}
                                    helperText={touched.remark && errors.remark}
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (
            <form onSubmit={handleSubmit}>

                <Grid container spacing={1}>

                    <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                        {
                            RenderFormDocument()
                        }
                        {
                            RenderFormCollection()
                        }
                    </Grid>


                    <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                        {
                            RenderFormLocation()
                        }

                        {
                            RenderFormProfiles()
                        }

                    </Grid>

                </Grid>
                <StyledCard sx={{ mt: 1 }}>
                    <Grid container rowSpacing={0}>
                        <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t(`items`)}
                                subTitle={t(`detailInformationAboutTheItemsInvolved`)}
                            />

                        </Grid>

                        <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                            {
                                RenderRows()
                            }

                            {/* <FieldArray
                                name="rows"
                                render={(arrayHelpers) => {
                                    
                                    return RenderRow({ values, errors, touched, handleChange, setFieldValue, arrayHelpers})
                                
                                }}
                            /> */}
                        </Grid>

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>

            </form>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateReplacementDelivery })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("updateReplacementDelivery")}
                breadcrumbs={[
                    {
                        displayText: t("replacementDeliveries"),
                        props: {
                            href: getRoute('documents/replacementDeliveries')
                        }
                    },
                    {
                        displayText: t("updateReplacementDelivery"),
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    (
                        <CommonRestricted />
                    )
            }

            {
                RenderDocumentTaxPreview()
            }

        </Box>
    )
}